import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import { useFarms } from "@/lib/repo";

const FarmCtx = createContext(null);

const storageKey = (uid) => `cv:activeFarm:${uid || "anon"}`;

// Iniciales legibles para el botón/avatar de la finca (ej. "Finca La Esperanza" -> "LE")
export function farmInitials(name) {
  if (!name) return "FN";
  const words = name.trim().split(/\s+/).filter(Boolean);
  const meaningful = words.length > 1 ? words.filter((w) => !/^fincas?$/i.test(w)) : words;
  const src = meaningful.length ? meaningful : words;
  return src.slice(0, 3).map((w) => w[0]).join("").toUpperCase();
}

export function FarmProvider({ children }) {
  const { user } = useAuth();
  // Lee de Dexie (offline-first) — funciona sin conexión con lo último sincronizado.
  const allFarms = useFarms();
  const farms = (user?.role === "handler" || user?.role === "vet")
    ? allFarms.filter((f) => (user?.assigned_farm_ids || []).includes(f.id))
    : allFarms;
  const [activeFarmId, setActiveFarmIdState] = useState(null);
  const loading = false;

  useEffect(() => {
    if (!user || farms.length === 0) return;
    const saved = localStorage.getItem(storageKey(user.id));
    let next = null;
    if (saved && farms.some((f) => f.id === saved)) next = saved;
    else if (farms.length === 1) next = farms[0].id;
    setActiveFarmIdState((cur) => (cur && farms.some((f) => f.id === cur)) ? cur : next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, farms.length]);

  const setActiveFarmId = useCallback((id) => {
    setActiveFarmIdState(id);
    if (user) localStorage.setItem(storageKey(user.id), id || "");
  }, [user]);

  const activeFarm = farms.find((f) => f.id === activeFarmId) || null;

  return (
    <FarmCtx.Provider value={{ farms, activeFarm, activeFarmId, setActiveFarmId, loading }}>
      {children}
    </FarmCtx.Provider>
  );
}

export function useFarm() {
  return useContext(FarmCtx);
}
